<template>
    <div :class="{ 'server-popup-panel-mode': mode === 'panel' }">
        <vvo v-slot="v">
            <!-- 헤더 -->
            <div class="row form-group">
                <div class="col detail-title">
                    <span class="title">{{ newServer.serverName || "데이터 수집기" }}</span>
                    <span class="badge title"> {{ newServer.serverType || "Server" }} </span>
                    <span class="badge id"> {{ newServer.serverIdx || "신규" }} </span>
                </div>
            </div>

            <!-- 탭 컨테이너 -->
            <div v-if="isUpdate" class="tab-container overflowStyle">
                <!-- 탭 버튼 -->
                <ul class="custom-tabs">
                    <li v-for="(tab, index) in tabs" :key="index" class="custom-tab"
                        :class="{ active: activeTab === index }" @click="handleTabClick(index)">
                        <span class="mr-1"><app-icon :icon="tab.icon" /></span>
                        <span>{{ tab.label }}</span>
                    </li>
                </ul>

                <!-- 탭 콘텐츠 -->
                <div class="tab-content-container p-3 mb-0 overflowStyle">
                    <!-- 하위 계측기 목록 탭 -->
                    <div v-if="activeTab === 0" :key="'tab-0'" class="facility-list-content">
                        <!-- 서브 탭 버튼 -->
                        <div class="controls-wrapper">
                            <!-- 계측기 정보 탭 -->
                            <div class="facility-mode-controls">
                                <label class="tab-label">표시 방법 :</label>
                                <select v-model="activeSubTab" class="tab-select" @change="handleSubTabChange">
                                    <option v-for="(subTab, index) in subTabs" :key="index" :value="index">
                                        {{ subTab.label }}
                                    </option>
                                </select>
                            </div>
                            <!-- 엑셀 작업 -->
                            <div v-if="activeSubTab === 1" class="excel-section">
                                <button class="excel-btn upload" @click="triggerFileUpload">
                                    <i class="fa fa-upload"></i> 관리정보 업로드
                                </button>
                                <button class="excel-btn download" @click="downloadFacilityOpInfo">
                                    <i class="fa fa-download"></i> 관리정보 다운로드
                                </button>
                            </div>
                            <!-- 데이터 다운로드 버튼 -->
                            <div v-if="activeSubTab === 0" class="excel-section">
                                <button class="excel-btn download" @click="openBulkDownloadPopup" :disabled="selectedFacilityCount === 0">
                                    <i class="fa fa-download"></i> 데이터 다운로드 ({{ selectedFacilityCount }})
                                </button>
                            </div>
                        </div>
                        <input ref="fileInput" type="file" @change="handleFileChange" accept=".xlsx,.xls"
                            style="display: none" />

                        <!-- 서브 탭 콘텐츠 -->
                        <div class="sub-tab-content-minimal">
                            <!-- 계측기 이력정보 서브 탭 -->
                            <div v-if="activeSubTab === 0" class="facility-history-content">
                                <div v-if="childrenData && childrenData.children && childrenData.children.length > 0"
                                    class="facility-container">
                                    <div class="table-container">
                                        <TableList 
                                            ref="facilityTableList"
                                            :key="`history-table-${selectedGroupField || 'no-group'}`"
                                            :columns="facilityColumns" :rows="filteredFacilityRows"
                                            :key-field="'facilityName'" :mode="'edit'" :use-filter="true"
                                            :is-sort="true" :fixed-header="true" :custom-max-height="'480px'"
                                            :trans-codes="facilityTransCodes"
                                            :group-field="isValidGroupField ? selectedGroupField : ''"
                                            :borderRadius="'0px 0px 6px 6px'"
                                            @on-selected-rows-change="handleSelectedRowsChange" />
                                    </div>
                                </div>

                                <div v-else class="no-facility">
                                    <div class="no-data-message">
                                        <i class="fa fa-info-circle"></i>
                                        <span>등록된 부하가 없습니다.</span>
                                    </div>
                                </div>
                            </div>

                            <!-- 계측기 관리정보 서브 탭 -->
                            <div v-else-if="activeSubTab === 1" class="facility-management-content">
                                <div v-if="facilityOpInfoList && facilityOpInfoList.length > 0"
                                    class="facility-container">
                                    <div class="table-container">
                                        <TableList :key="`management-table-${selectedGroupField || 'no-group'}`"
                                            :columns="facilityManagementColumns" :rows="facilityManagementRows"
                                            :key-field="'facilityName'" :mode="'view'" :use-filter="true"
                                            :is-sort="true" :fixed-header="true" :custom-max-height="'480px'"
                                            :trans-codes="facilityManagementTransCodes"
                                            :borderRadius="'0px 0px 6px 6px'"
                                            @row-click="handleFacilityOpInfoRowClick" />
                                    </div>
                                </div>

                                <div v-else class="no-facility">
                                    <div class="no-data-message">
                                        <i class="fa fa-info-circle"></i>
                                        <span>등록된 부하가 없습니다.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 서버 정보 탭 -->
                    <div v-else-if="activeTab === 1" :key="'tab-1'" class="mt-2" style="padding: 10px">
                        <div class="row form-group">
                            <div class="col-12">
                                <label><strong>데이터 수집기명</strong></label>
                                <valid-input :vid="'데이터 수집기명'" :inputType="'text'"
                                    :inputValue.sync="newServer.serverName" :rules="rules.COLLECT_DATA_EQUIP_NAME_RULE"
                                    :errorMessage="validMessage.COMMON_BOTH_LENGTH_VALID_MESSAGE(1, 15)"
                                    :placeholder="$t('데이터 수집기명을 입력하세요.')"></valid-input>
                            </div>
                        </div>
                        <!-- 데이터 수집기 연결 -->
                        <!-- GS 테스트를 위한 임시 작업, 250616 -->
                        <!-- v-xe-pm.E -->
                        <div class="row form-group">
                            <div class="col-12">
                                <label><strong>데이터 수집기 연결</strong></label>
                            </div>
                            <div class="col-10">
                                <select class="form-control" v-model="newClientIdx" @change="onChangeAgent">
                                    <option value="" selected>없음</option>
                                    <option v-for="(client, i) in clients" :key="i" :value="client.clientIdx">
                                        <!-- {{ client.ip }} / {{ client.mac }} / {{ client.hostname }} -->
                                        {{
                                            `[${client.modDate}] ${client.ip} / ${client.mac} / ${client.hostname}`
                                        }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-2">
                                <!-- <button class="btn btn-lime m-l-5" @click="onRemoveAgent()" v-b-tooltip.hover :title="$t('에이전트 제거')">
          <i class="fa fa-times"> </i>
        </button> -->
                                <!-- <button class="btn btn-primary m-l-5" @click="onSaveAgent()" v-b-tooltip.hover :title="$t('에이전트 변경')">
          <i class="fa fa-times"> </i>
          {{$t('저장')}}
        </button> -->
                                <button :disabled="isEmpty(client) || isEmpty(client.clientIdx)" class="btn btn-yellow"
                                    @click="onRestartAgent()" v-b-tooltip.hover :title="$t('에이전트 재시작')"
                                    style="width: 100%">
                                    <i class="fa fa-sync"></i>
                                    {{ $t("재시작") }}
                                </button>
                            </div>
                        </div>

                        <!-- 서버 상태
    { 
      "cpu": { "total": 100, "cpuUsed": 1.94, "cpuAvrg": 4.082 }, 
      "ram": { "ramTotal": 31.93, "ramUsed": 16.7, "ramFree": 15.24 }, 
      "hdd": { "hddTotal": 4172.12, "hddUsed": 609.02, "hddFree": 3563.1 }, 
      "keepAlive": "20201021200821" 
    } -->
                        <div class="row">
                            <div class="col-12">
                                <label><strong>NIC</strong></label>
                                <table class="table table-bordered">
                                    <colgroup>
                                        <col width="20%" />
                                        <col width="80%" />
                                    </colgroup>
                                    <tr>
                                        <th class="text-center bg-light">
                                            <trans>IP</trans>
                                        </th>
                                        <td>{{ newServer.ip ?? "-" }}</td>
                                    </tr>
                                    <tr>
                                        <th class="text-center bg-light">
                                            <trans>MAC</trans>
                                        </th>
                                        <td>{{ newServer.mac ?? "-" }}</td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                        <div class="row" v-if="status">
                            <div class="col-6">
                                <label><strong>메모리</strong></label>
                                <table class="table table-bordered">
                                    <colgroup>
                                        <col width="20%" />
                                        <col width="80%" />
                                    </colgroup>
                                    <tr>
                                        <th class="text-center bg-light">
                                            <trans>전체</trans>
                                        </th>
                                        <td>
                                            <div class="progress-bar-container">
                                                <div class="custom-progress" :style="{
                                                    '--progress-color':
                                                        getRamPercentage() > 80 ? '#dc3545' : '#28a745',
                                                    height: '25px',
                                                }">
                                                    <div class="progress-bar"
                                                        :style="{ width: getRamPercentage() + '%' }">
                                                        {{ formatRamUsage() }}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th class="text-center bg-light">
                                            <trans>사용</trans>
                                        </th>
                                        <td>{{ status.ram.used ?? "-" }}</td>
                                    </tr>
                                    <tr>
                                        <th class="text-center bg-light">
                                            <trans>여유</trans>
                                        </th>
                                        <td>{{ status.ram.free ?? "-" }}</td>
                                    </tr>
                                    <tr v-if="status.ram.isWarn">
                                        <td colspan="2" class="text-red text-center">
                                            <i class="fa fa-exclamation-triangle"></i>
                                            <trans>메모리사용량이 과도합니다.</trans>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            <div class="col-6">
                                <label><strong>저장소</strong></label>
                                <table class="table table-bordered">
                                    <colgroup>
                                        <col width="20%" />
                                        <col width="80%" />
                                    </colgroup>
                                    <tr>
                                        <th class="text-center bg-light">
                                            <trans>전체</trans>
                                        </th>
                                        <td>
                                            <div class="progress-bar-container">
                                                <div class="custom-progress" :style="{
                                                    '--progress-color':
                                                        getHddPercentage() > 80 ? '#dc3545' : '#007bff',
                                                    height: '25px',
                                                }">
                                                    <div class="progress-bar"
                                                        :style="{ width: getHddPercentage() + '%' }">
                                                        {{ formatHddUsage() }}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th class="text-center bg-light">
                                            <trans>사용</trans>
                                        </th>
                                        <td>{{ status.hdd.used ?? "-" }}</td>
                                    </tr>
                                    <tr>
                                        <th class="text-center bg-light">
                                            <trans>여유</trans>
                                        </th>
                                        <td>{{ status.hdd.free ?? "-" }}</td>
                                    </tr>
                                    <tr v-if="status.hdd.isWarn">
                                        <td colspan="2" class="text-red text-center">
                                            <i class="fa fa-exclamation-triangle"></i>
                                            <trans>디스크공간이 얼마남지 않았습니다.</trans>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                        <div class="row form-group" v-if="status">
                            <div class="col-lg-12">
                                <label> <strong>최종실행일</strong> </label>
                                <div class="form-control" readonly v-if="keepAlive">
                                    <span class="pull-left"> {{ keepAlive.passedTime() }} </span>
                                    <span class="pull-right"> {{ keepAlive.format("yyyy-MM-dd HH:mm") }} </span>
                                </div>
                                <div class="form-control" readonly v-else>Empty</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- 기존 서버 정보 (신규 등록시) -->
            <template v-else>
                <div class="row form-group">
                    <div class="col-12">
                        <label><strong>데이터 수집기명</strong></label>
                        <valid-input :vid="'데이터 수집기명'" :inputType="'text'" :inputValue.sync="newServer.serverName"
                            :rules="rules.COLLECT_DATA_EQUIP_NAME_RULE"
                            :errorMessage="validMessage.COMMON_BOTH_LENGTH_VALID_MESSAGE(1, 15)"
                            :placeholder="$t('데이터 수집기명을 입력하세요.')"></valid-input>
                    </div>
                </div>
            </template>

            <div v-if="activeTab === 1" slot="modal-footer" class="w-100">
                <button v-xe-pm.E class="btn btn-danger" v-if="isUpdate && (isEmpty(childrens) || childrens == 0)"
                    @click="onDeleteServer()">
                    <i class="fa fa-times"></i> {{ $t("삭제") }}
                </button>
                <button class="btn btn-primary pull-right" @click="onValidate(v, onSaveServer, newServer)">
                    <i class="fa fa-save"></i> {{ $t("저장") }}
                </button>
            </div>
            <FacilityInfoPopup ref="FacilityInfoPopup" :facilityIdx="getFacilityOpInfoIdx" />
            <FacilityBulkDownloadPopup
                ref="FacilityBulkDownloadPopup"
                :selectedFacilities="selectedFacilities"
                :serverIdx="server?.serverIdx"
            />
        </vvo>
    </div>
</template>

<script>
import backEndApi from "../../../../api/backEndApi.js";
import * as rules from "@src/consts/ruleConsts.js";
import * as validMessage from "@src/consts/validMessageConsts.js";
import * as popupMessages from "@src/consts/popupMessageConsts";
import { mapState } from "vuex";
import TableList from "@src/views/component/v2.1/ListDetailView/TableList.vue";
import { extractFilenameFromResponse } from "@src/utils/exportExcelTitle.js";
import FacilityInfoPopup from "./FacilityInfoPopup.vue";
import FacilityBulkDownloadPopup from "./FacilityBulkDownloadPopup.vue";

// TODO: serverLog 테이블에 ip, mac 있어야 함...
// TODO: 저장소 부족체크  20%이하 경고, 서버에서 설정으로 관리해야 하고, 서버에서 로그삭제시 체크하여 경고알람을 발생시켜야 함...

// TODO: 에이전트 변경시, 다른 서버에서 사용중인 에인전트로 변경하게되면, 알려주어야 함.

// TODO: 서버로그에 IP, MAC 이 없어서, 어떤 서버의 내용인지 불명확함.   서버상태 업데이트가 1번만 되고 있음.. 에이전트에서 올라와야 함....
// 전반적으로 장비의 상태정보가 불명확함......

// TODO: 에이전트 재시작도 이상함..  에이전트가 접속중인지.. 재시작을 잘했는지에 대한 이벤트 로그확인이 어려움...

const config = {
    warningLimit: 20,
};

export default {
    props: ["server", "childrens", "childrenData", "save-done", "mode"],
    components: { TableList, FacilityInfoPopup, FacilityBulkDownloadPopup },
    data() {
        return {
            newServer: {},

            oldClientIdx: "",
            newClientIdx: "",
            clients: [],
            client: null,
            rules,
            validMessage,
            popupMessages,

            /* 탭 상태 */
            activeTab: 0,

            /* 탭 정의 */
            tabs: [
                {
                    label: "하위 계측기 목록",
                    icon: "fa fa-list",
                },
                {
                    label: "서버 정보",
                    icon: "fa fa-info-circle",
                },
            ],

            /* 서브 탭 정의 */
            subTabs: [
                {
                    label: "계측기 설정정보",
                    icon: "fa fa-history",
                },
                {
                    label: "계측기 관리정보",
                    icon: "fa fa-cogs",
                },
            ],

            /* 서브 탭 상태 */
            activeSubTab: 0,

            /* 테이블 필터링 및 그룹핑 */
            tableFilter: "",
            selectedGroupField: "meterTypeCode",

            /* 계측기 관리정보 */
            facilityOpInfoList: [],
            selectedFacilityOpInfoRow: null,

            /* 체크박스 선택 상태 */
            selectedFacilities: [],
        };
    },
    computed: {
        ...mapState({
            commonCodes: (state) => state.commonCodes,
            zoneInfo: (state) => state.zoneInfo,
        }),
        getFacilityOpInfoIdx() {
            return this.selectedFacilityOpInfoRow?.facilityIdx;
        },
        isUpdate() {
            return !this.isEmpty(this.server.serverIdx);
        },
        cpu() {
            return this.newServer &&
                !this.isEmpty(this.newServer.serverStatus) &&
                !this.isEmpty(this.newServer.serverStatus.cpu)
                ? this.newServer.serverStatus.cpu
                : {};
        },
        ram() {
            return this.newServer &&
                !this.isEmpty(this.newServer.serverStatus) &&
                !this.isEmpty(this.newServer.serverStatus.ram)
                ? this.newServer.serverStatus.ram
                : {};
        },
        hdd() {
            return this.newServer &&
                !this.isEmpty(this.newServer.serverStatus) &&
                !this.isEmpty(this.newServer.serverStatus.hdd)
                ? this.newServer.serverStatus.hdd
                : {};
        },
        status() {
            if (this.isEmptyObj(this.newServer.serverStatus)) return null;

            let ramFree = this.percentage(this.ram.ramFree, this.ram.ramTotal); // -1 반환은 계산할 수 없음임.
            let hddFree = this.percentage(this.hdd.hddFree, this.hdd.hddTotal);
            return {
                ram: {
                    total: this.storageSize(this.ram.ramTotal),
                    used: this.storageSize(this.ram.ramUsed),
                    free: ramFree > -1 ? ramFree + "%" : "",
                    isWarn: ramFree > -1 && ramFree <= config.warningLimit,
                },
                hdd: {
                    total: this.storageSize(this.hdd.hddTotal),
                    used: this.storageSize(this.hdd.hddUsed),
                    free: hddFree > -1 ? hddFree + "%" : "",
                    isWarn: hddFree > -1 && hddFree <= config.warningLimit,
                },
            };
        },
        keepAlive() {
            return this.newServer &&
                !this.isEmpty(this.newServer.serverStatus) &&
                !this.isEmpty(this.newServer.serverStatus.keepAlive)
                ? this.newServer.serverStatus.keepAlive
                : null;
        },
        facilityColumns() {
            return [
                {
                    label: "계측기명",
                    field: "facilityName",
                    sortable: true,
                },
                // facilityIdx를 hidden field로 추가 (표시는 안 하지만 선택된 행에 포함되도록)
                {
                    label: "",
                    field: "facilityIdx",
                    sortable: false,
                    hidden: true, // 표시하지 않음
                },
                {
                    label: "채널명",
                    field: "channelName",
                    sortable: true,
                },
                {
                    label: "사용여부",
                    field: "isUse",
                    sortable: true,
                },
                {
                    label: "장치 고유번호",
                    field: "deviceId",
                    sortable: true,
                },
                {
                    label: "활용 목적",
                    field: "purpsUseCode",
                    sortable: true,
                },
                {
                    label: "계측기 유형",
                    field: "meterTypeCode",
                    sortable: true,
                },
                {
                    label: "적용구역",
                    field: "applyZoneIdx",
                    sortable: true,
                },
                {
                    label: "설치일자",
                    field: "instDate",
                    sortable: true,
                },
            ];
        },
        facilityRows() {
            if (!this.childrenData || !this.childrenData.children) {
                return [];
            }

            // type이 facility인 데이터만 필터링
            const facilityData = [];

            const extractFacilities = (children, parentChannel = null) => {
                children.forEach((child) => {
                    // 채널 노드인 경우 채널명 저장
                    if (child.data && child.data.type === "channel") {
                        parentChannel = child.data.channelName;
                    }

                    // 설비 노드인 경우 데이터 추가
                    if (child.data && child.data.type === "facility") {
                        const facilityRow = {
                            facilityIdx: child.data.facilityIdx || null,
                            channelName: parentChannel || "-",
                            facilityName: child.data.facilityName || "-",
                            isUse: child.data.isUse || false,
                            deviceId: child.data.deviceId || "-",
                            purpsUseCode: child.data.purpsUseCode || null,
                            meterTypeCode: child.data.meterTypeCode || null,
                            applyZoneIdx: child.data.applyZoneIdx || null,
                            instDate: child.data.instDate || null,
                        };
                        // 고유 키 생성 (facilityIdx가 없을 경우를 대비)
                        if (!facilityRow.facilityIdx) {
                            facilityRow._uniqueKey = `${facilityRow.channelName}_${facilityRow.facilityName}_${facilityRow.deviceId}`;
                        }
                        facilityData.push(facilityRow);
                    }

                    // 재귀적으로 하위 children도 검색
                    if (child.children && child.children.length > 0) {
                        extractFacilities(child.children, parentChannel);
                    }
                });
            };

            extractFacilities(this.childrenData.children);
            return facilityData;
        },
        facilityTransCodes() {
            return [
                {
                    field: "isUse",
                    codes: [
                        { value: true, text: "사용" },
                        { value: false, text: "미사용" },
                    ],
                },
                {
                    field: "purpsUseCode",
                    codes: this.commonCodes?.purpsUseCode || [],
                },
                {
                    field: "meterTypeCode",
                    codes: this.commonCodes?.meterTypeCode || [],
                },
                {
                    field: "applyZoneIdx",
                    codes: this.zoneInfo || [],
                },
            ];
        },
        filteredFacilityRows() {
            if (!this.facilityRows || this.facilityRows.length === 0) {
                return [];
            }

            let filtered = [...this.facilityRows];

            // 필터링 적용
            if (this.tableFilter && this.tableFilter.trim() !== "") {
                const filterValue = this.tableFilter.toLowerCase().trim();
                filtered = filtered.filter((row) => {
                    return (
                        (row.facilityName && row.facilityName.toLowerCase().includes(filterValue)) ||
                        (row.channelName && row.channelName.toLowerCase().includes(filterValue)) ||
                        (row.deviceId && row.deviceId.toLowerCase().includes(filterValue))
                    );
                });
            }

            return filtered;
        },
        // 그룹핑 필드가 유효한지 확인
        isValidGroupField() {
            return this.selectedGroupField && this.selectedGroupField.trim() !== "";
        },

        // 계측기 관리정보 컬럼 정의
        facilityManagementColumns() {
            return [
                {
                    label: "계측기명",
                    field: "facilityName",
                    sortable: true,
                },

                {
                    label: "제조사",
                    field: "manufacturer",
                    sortable: true,
                },
                {
                    label: "모델명",
                    field: "modelNumber",
                    sortable: true,
                },
                {
                    label: "시리얼 넘버",
                    field: "serialNumber",
                    sortable: true,
                },
                {
                    label: "설치장소",
                    field: "location",
                    sortable: true,
                },
                {
                    label: "정격용량",
                    field: "ratedCapacity",
                    sortable: true,
                },
                {
                    label: "정격 COP",
                    field: "ratedCOP",
                    sortable: true,
                },
                {
                    label: "수정일시",
                    field: "updDt",
                    sortable: true,
                },
                {
                    label: "설명",
                    field: "description",
                    sortable: true,
                },
            ];
        },

        // 계측기 관리정보 데이터
        facilityManagementRows() {
            if (!this.facilityOpInfoList || this.facilityOpInfoList.length === 0) {
                return [];
            }

            return this.facilityOpInfoList.map((item) => ({
                facilityName: item.facilityName || "-",
                manufacturer: item.opInfo?.manufacturer || "-",
                modelNumber: item.opInfo?.modelNumber || "-",
                serialNumber: item.opInfo?.serialNumber || "-",
                location: item.opInfo?.location || "-",
                ratedCapacity: item.opInfo?.ratedCapacity
                    ? `${item.opInfo?.ratedCapacity} ${item.opInfo?.capacityUnit || ""}`
                    : "-",
                ratedCOP: item.opInfo?.ratedCOP || "-",
                updDt: this.formatRegDt(item.opInfo?.updDt) || "-",
                description: this.codeChangelengthText(item.opInfo?.description, 7) || "-",
                facilityIdx: item.facilityIdx || "-",
            }));
        },

        // 계측기 관리정보 변환 코드
        facilityManagementTransCodes() {
            return [];
        },
        selectedFacilityCount() {
            return this.selectedFacilities.length;
        },
    },
    watch: {
        "server.serverIdx": function () {
            this.initSetting();
        },
        activeSubTab: function (newVal) {
            if (newVal === 1) {
                this.searchFacilityOpInfo();
            }
        },
    },
    mounted() {
        this.initSetting();
    },
    methods: {
        searchFacilityOpInfo() {
            backEndApi.facility.searchFacilityOpInfo({ serverIdx: this.server.serverIdx }).then(({ data }) => {
                this.facilityOpInfoList = data;
            });
        },
        handleFacilityOpInfoRowClick(row) {
            // facilityOpInfoList에서 해당 facilityIdx를 가진 원본 데이터를 찾아서 설정
            const originalData = this.facilityOpInfoList.find((item) => item.facilityIdx === row.facilityIdx);
            this.selectedFacilityOpInfoRow = originalData || row;
            this.$refs.FacilityInfoPopup.show();
        },

        // 파일 업로드 트리거
        triggerFileUpload() {
            this.$refs.fileInput.click();
        },

        // 파일 선택 시 처리
        handleFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.uploadFacilityOpInfo(file);
                // 파일 입력 초기화
                this.$refs.fileInput.value = "";
            }
        },

        // 파일 업로드 처리
        uploadFacilityOpInfo(file) {
            if (!file) {
                this.alertNoti("파일을 선택해주세요.");
                return;
            }

            // 파일 형식 검증
            const allowedTypes = [
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
                "application/vnd.ms-excel", // .xls
            ];

            if (!allowedTypes.includes(file.type)) {
                this.alertNoti("엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.");
                return;
            }

            // 업로드 진행
            this.alertQuestion("파일을 업로드하시겠습니까?").then((result) => {
                if (!result.value) return;
                this.executeFileUpload(file);
            });
        },

        // 파일 다운로드 처리
        downloadFacilityOpInfo() {
            const serverIdx = this.childrenData.data.serverIdx;

            this.alertQuestion("계측기 관리정보를 다운로드하시겠습니까?").then((result) => {
                if (!result.value) return;

                // 현재 선택된 서버 정보로 다운로드
                this.executeFileDownload(serverIdx);
            });
        },
        // 계측기 데이터 새로고침
        refreshFacilityData() {
            // 부모 컴포넌트에 데이터 새로고침 요청
            this.$emit("refresh-data");
        },

        // 실제 파일 업로드 실행
        async executeFileUpload(file) {
            try {
                const { data } = await backEndApi.facility.uploadFacilityOpInfo(file);

                this.alertNoti(data.message);
                // 업로드 성공 후 데이터 새로고침
                this.refreshFacilityData();
            } catch (error) {
                console.error("Upload error:", error);
                this.alertNoti("파일 업로드 중 오류가 발생했습니다.");
            }
        },

        // 실제 파일 다운로드 실행

        async executeFileDownload(serverIdx) {
            try {
                const response = await backEndApi.facility.downloadFacilityOpInfo({ serverIdx: serverIdx });

                if (response.status === 200 && response.data) {
                    let fileName = `계측기 관리정보.xlsx`;

                    const blob = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;

                    fileName = extractFilenameFromResponse(response, fileName);
                    link.download = fileName;

                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    this.alertNoti("계측기 관리정보 다운로드가 시작되었습니다.");
                } else {
                    throw new Error(response.data.message);
                }
            } catch (e) {
                this.alertWarning(e.message);
                console.error(e);
            }
        },

        // regDt를 연월일까지만 표시
        formatRegDt(val) {
            if (global.xe.isEmpty(val)) return "-";

            // 문자열인 경우 Date 객체로 변환
            if (typeof val === "string") {
                const date = new Date(val);
                if (isNaN(date.getTime())) return val; // 유효하지 않은 날짜면 원본 반환
                return date.toISOString().split("T")[0]; // yyyy-MM-dd 형식으로 반환
            }

            // 이미 Date 객체인 경우
            return val.format("yyyy-MM-dd");
        },
        // 텍스트가 너무 길경우 자르고 ... 처리
        codeChangelengthText(val, len = 50, defaultStr = "") {
            if (global.xe.isEmpty(val)) return defaultStr;
            if (val.length > len) return val.substring(0, len) + "...";
            return val;
        },
        /* 탭 수동 클릭 */
        handleTabClick(idx) {
            if (this.activeTab === idx) return;
            this.activeTab = idx;

            // 탭 변경 시 필터와 그룹핑 초기화 (하위 계측기 목록 탭이 이제 0번)
            if (idx === 0) {
                this.tableFilter = "";
                this.selectedGroupField = "meterTypeCode";
                this.activeSubTab = 0; // 서브 탭도 초기화
            }
        },

        /* 서브 탭 수동 클릭 */
        handleSubTabClick(idx) {
            if (this.activeSubTab === idx) return;
            this.activeSubTab = idx;
        },

        /* 서브 탭 select 변경 */
        handleSubTabChange() {
            // select 박스 값이 변경되면 자동으로 activeSubTab이 업데이트됨
            // 추가 로직이 필요한 경우 여기에 작성
        },

        initSetting() {
            this.newServer = {};

            if (this.isUpdate) {
                backEndApi.server.getServer(this.server.serverIdx).then(({ data }) => {
                    if (this.$err(data)) return;

                    this.newServer = data;
                });

                // 에이전트 연결
                this.bindAgent();
            }
        },
        //#region 서버정보
        onSaveServer() {
            this.isUpdate ? this.updateServer() : this.insertServer();
        },
        insertServer() {
            this.alertQuestion(popupMessages.COMMON_SAVE_CONFIRM_POPUP_MESSAGE).then((result) => {
                if (!result.value) return;

                backEndApi.server.insertServer(this.newServer).then(({ data }) => {
                    if (this.$err(data)) return;

                    this.alertNoti(popupMessages.COMMON_SAVE_POPUP_MESSAGE);
                    this.$emit("save-done");
                });
            });
        },
        updateServer() {
            this.alertQuestion(popupMessages.COMMON_UPDATE_CONFIRM_POPUP_MESSAGE).then((result) => {
                if (!result.value) return;

                backEndApi.server.updateServer(this.newServer).then(({ data }) => {
                    if (this.$err(data)) return;

                    this.alertNoti(popupMessages.COMMON_UPDATE_POPUP_MESSAGE);
                    this.$emit("save-done");
                });
            });
        },
        onDeleteServer() {
            // TODO: 하위 정보가 있으면 삭제할 수 없습니다.

            this.alertConfirmWarning(popupMessages.COMMON_DELETE_CONFIRM_POPUP_MESSAGE).then((result) => {
                if (!result.value) return;

                backEndApi.server.deleteServer(this.newServer.serverIdx).then(({ data }) => {
                    if (this.$err(data)) return;

                    this.alertNoti(popupMessages.COMMON_DELETE_POPUP_MESSAGE);
                    this.$emit("save-done");
                });
            });
        },
        //#endregion

        //#region 에이전트

        bindAgent() {
            this.clients.clear();
            backEndApi.client.searchClient().then(({ data }) => {
                if (this.$err(data)) return;

                for (let item of data.sort((a, b) => (a.ip < b.ip ? -1 : a.ip < b.ip ? 1 : 0))) {
                    this.clients.push(item);
                }
            });

            this.client = null;
            this.oldClientIdx = "";
            this.newClientIdx = "";

            if (this.isUpdate) {
                backEndApi.client.getClientByServerIdx(this.server.serverIdx).then(({ data }) => {
                    if (this.$err(data)) return;

                    this.client = data;
                    this.oldClientIdx = data.clientIdx;
                    this.newClientIdx = data.clientIdx;
                });
            }
        },
        // onRemoveAgent() {
        //   this.newClientIdx = "";
        // },
        // onSaveAgent(){
        //   this.alertConfirmWarning("저장하시겠습니까?", "에이전트").then((result) => {
        //     if (!result.value) return;

        //     backEndApi.client.applyClient({ serverIdx: this.newServer.serverIdx, oldClientIdx: this.oldClientIdx, newClientIdx: this.newClientIdx });

        //   });
        // },
        onChangeAgent() {
            const that = this;

            this.alertQuestion(popupMessages.POINT_DATA_COLLECTION_AGENT_CHANGE_POPUP_MESSAGE).then((result) => {
                if (!result.value) {
                    that.newClientIdx = that.oldClientIdx;
                    return;
                }

                backEndApi.client
                    .applyClient({
                        serverIdx: this.server.serverIdx,
                        oldClientIdx: this.oldClientIdx,
                        newClientIdx: this.newClientIdx,
                    })
                    .then(({ data }) => {
                        if (this.$err(data)) return;

                        this.alertNoti(popupMessages.COMMON_UPDATE_POPUP_MESSAGE);
                        this.bindAgent();
                    });
            });
        },
        onRestartAgent() {
            if (this.isEmpty(this.client) || this.isEmpty(this.client.clientIdx)) {
                this.alertNoti(popupMessages.POINT_DATA_COLLECTION_AGENT_RESTART_ERROR_POPUP_MESSAGE); // TODO: 오류메세지로 바꿔야 함.
                return;
            }

            this.alertQuestion(popupMessages.POINT_DATA_COLLECTION_AGENT_RESTART_POPUP_MESSAGE).then((result) => {
                if (!result.value) return;

                backEndApi.client.restartAgent(this.client.clientIdx).then(({ data }) => {
                    if (this.$err(data)) return;

                    this.alertNoti(popupMessages.POINT_DATA_COLLECTION_AGENT_RESTART_SEND_POPUP_MESSAGE);
                });
            });
        },
        // 메모리/저장소 프로그래스바 계산 헬퍼
        getRamPercentage() {
            const ram = this.newServer?.serverStatus?.ram;
            if (!ram || !ram.ramTotal || !ram.ramUsed) return 0;
            const percentage = (ram.ramUsed / ram.ramTotal) * 100;
            return Math.min(Math.round(percentage), 100);
        },
        formatRamUsage() {
            const ram = this.newServer?.serverStatus?.ram;
            if (!ram || !ram.ramTotal || !ram.ramUsed) return "-";
            const usedPercent = ((ram.ramUsed / ram.ramTotal) * 100).toFixed(1);
            const used = Number(ram.ramUsed).toFixed(1);
            const total = Number(ram.ramTotal).toFixed(1);
            return `${used}GB / ${total}GB (${usedPercent}%)`;
        },
        getHddPercentage() {
            const hdd = this.newServer?.serverStatus?.hdd;
            if (!hdd || !hdd.hddTotal || !hdd.hddUsed) return 0;
            const percentage = (hdd.hddUsed / hdd.hddTotal) * 100;
            return Math.min(Math.round(percentage), 100);
        },
        formatHddUsage() {
            const hdd = this.newServer?.serverStatus?.hdd;
            if (!hdd || !hdd.hddTotal || !hdd.hddUsed) return "-";
            const usedPercent = ((hdd.hddUsed / hdd.hddTotal) * 100).toFixed(1);
            const used = Number(hdd.hddUsed).toFixed(1);
            const total = Number(hdd.hddTotal).toFixed(1);
            return `${used}GB / ${total}GB (${usedPercent}%)`;
        },
        handleSelectedRowsChange(selectedRows) {
            console.log("handleSelectedRowsChange called", {
                selectedRows,
                facilityRows: this.facilityRows,
                selectedRowsLength: Array.isArray(selectedRows) ? selectedRows.length : 0
            });

            // vue-good-table이 객체로 감싸서 전달할 수 있음
            let actualSelectedRows = selectedRows;
            if (selectedRows && typeof selectedRows === 'object' && !Array.isArray(selectedRows)) {
                // selectedRows.selectedRows 형태인 경우
                if (selectedRows.selectedRows && Array.isArray(selectedRows.selectedRows)) {
                    actualSelectedRows = selectedRows.selectedRows;
                } else {
                    // 다른 형태일 수 있음
                    this.selectedFacilities = [];
                    return;
                }
            }

            // selectedRows가 배열인지 확인
            if (!Array.isArray(actualSelectedRows)) {
                this.selectedFacilities = [];
                return;
            }

            // 선택된 행에서 facilityIdx 추출
            this.selectedFacilities = actualSelectedRows.map((row) => {
                // vue-good-table이 반환하는 row는 vgtRows의 구조를 따를 수 있음
                // 원본 데이터를 찾기 위해 여러 방법 시도
                let originalRow = null;
                
                // 1. facilityIdx로 먼저 찾기 (가장 정확)
                if (row.facilityIdx) {
                    originalRow = this.facilityRows.find((r) => r.facilityIdx === row.facilityIdx);
                }
                
                // 2. channelName과 facilityName 조합으로 찾기 (더 정확)
                if (!originalRow && row.facilityName && row.channelName) {
                    originalRow = this.facilityRows.find(
                        (r) => r.facilityName === row.facilityName && r.channelName === row.channelName
                    );
                }
                
                // 3. facilityName으로 찾기 (마지막 수단)
                if (!originalRow && row.facilityName) {
                    originalRow = this.facilityRows.find((r) => r.facilityName === row.facilityName);
                }
                
                // 4. _uniqueKey로 찾기 (facilityIdx가 없는 경우)
                if (!originalRow && row._uniqueKey) {
                    originalRow = this.facilityRows.find((r) => r._uniqueKey === row._uniqueKey);
                }
                
                // 원본 데이터를 찾았으면 그것을 사용, 없으면 row 자체 사용
                const result = originalRow || row;
                
                // facilityIdx가 있는지 확인
                if (!result.facilityIdx) {
                    console.warn("facilityIdx가 없는 행:", result);
                }
                
                return result;
            }).filter((facility) => facility && facility.facilityIdx); // facilityIdx가 있는 것만 필터링
            
            console.log("selectedFacilities after processing:", this.selectedFacilities);
        },
        openBulkDownloadPopup() {
            console.log("openBulkDownloadPopup called", {
                selectedFacilities: this.selectedFacilities,
                selectedFacilityCount: this.selectedFacilityCount,
                refExists: !!this.$refs.FacilityBulkDownloadPopup
            });
            
            if (this.selectedFacilities.length === 0) {
                this.alertWarning("다운로드할 계측기를 선택해주세요.");
                return;
            }
            
            // 팝업 컴포넌트의 show 메서드 호출
            this.$nextTick(() => {
                if (this.$refs.FacilityBulkDownloadPopup) {
                    this.$refs.FacilityBulkDownloadPopup.show();
                } else {
                    console.error("FacilityBulkDownloadPopup ref를 찾을 수 없습니다.");
                    this.alertWarning("팝업을 열 수 없습니다. 페이지를 새로고침해주세요.");
                }
            });
        },
        //#endregion
    },
};
</script>

<style scoped>
.server-popup-panel-mode {
    width: 86%;
    height: 78vh;
    padding: 15px;
    border-radius: 10px;
    background-color: white;
    box-shadow: rgba(0, 0, 0, 0.15) 0px 2px 8px;
}

.detail-title {
    padding: 0 !important;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: 1rem;
    font-weight: 600;
}

.title {
    font-size: 14px;
    font-weight: 600;
    margin-left: 10px;
    margin-right: 10px;
}

.badge {
    font-size: 12px;
    font-weight: 500;
    margin-right: 4px;
    display: inline-block;
    min-width: 20px;
    padding: 0px 10px 0px 10px;
    color: #fff;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 22px;
    border-radius: 8px;
}

.badge.title {
    background-color: #ff6d00;
    color: #fff;
}

.badge.id {
    background-color: #546e7a;
    color: #fff;
}

.badge.option {
    background-color: #cfd8dc;
    color: #000;
}

/* tabs */
.custom-tabs {
    display: flex;
    margin: 0;
    padding: 0;
    list-style: none;
    background: #fff;
}

.custom-tab {
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 500;
    background: transparent;
    color: #6c757d;
    border: none;
    border-bottom: 2px solid transparent;
    transition: all 0.2s ease;
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 120px;
    justify-content: center;
}

.custom-tab:hover {
    color: #495057;
    background: #f8f9fa;
}

.custom-tab.active {
    color: #002060;
    font-weight: 600;
    border-bottom-color: #002060;
}

/* tab container */
.tab-container {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
    border-radius: 8px;
}

.tab-content-container {
    flex: 1 1 auto;
    overflow: auto;
    overflow-x: hidden;
    background: #fff;
    border-radius: 0 0 8px 8px;
    position: relative;
    padding: 1.5rem;
    border-top: 1px solid #f1f3f4;
}

/* 부하 목록 탭 스타일 */
.facility-list-content {
    padding: 0;
    width: 100%;
}

.facility-container {
    background: #ffffff;
    border-radius: 8px;
    /* padding: 10px; */
    min-height: 200px;
}

.facility-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e9ecef;
}

.section-title {
    font-size: 13px;
    font-weight: 600;
    margin: 0;
    color: #495057;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: 0.5rem;
}

.facility-info {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 12px;
}

.total-count {
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    background: #f8f9fa;
    padding: 4px 12px;
    border-radius: 16px;
}

.table-container {
    background: #ffffff;
}

.table-controls {
    background: #f2f3f4;
    border-radius: 8px 8px 0 0;
    padding: 15px 15px 10px 15px;
    border: 1px solid #e9ecef;
}

.table-controls .control-label {
    font-size: 12px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 5px;
}

.table-controls .control-label i {
    margin-right: 5px;
    color: #6c757d;
}

.table-controls .form-control {
    border-radius: 6px;
    border: 1px solid #ced4da;
    font-size: 13px;
}

.table-controls .form-control:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.table-controls .text-info {
    font-size: 12px;
    font-weight: 500;
}

.table-controls .text-info i {
    margin-right: 5px;
}

/* text-center 클래스 스타일 */
.text-center {
    text-align: center !important;
}

/* 추가 정렬 옵션들 */
.text-left {
    text-align: left !important;
}

.text-right {
    text-align: right !important;
}

.facility-name {
    font-weight: 600;
    color: #495057;
}

.status-badge .status-active {
    background: #d4edda;
    color: #155724;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.status-badge .status-inactive {
    background: #f8d7da;
    color: #721c24;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.date-format {
    color: #6c757d;
    font-size: 12px;
}

.no-facility {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #dee2e6;
}

.no-data-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: #6c757d;
    font-size: 14px;
}

.no-data-message i {
    font-size: 2rem;
    color: #adb5bd;
}

/* 서브 탭 버튼 스타일 */
.controls-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 15px 0 0 0;
    padding: 15px;
    background: #f2f3f4;
    border-radius: 8px 8px 0 0;
    border: 1px solid #e9ecef;
}

/* 탭 섹션 */
.facility-mode-controls {
    margin-left: 8px;
}

.tab-label {
    font-size: 12px;
    font-weight: 600;
    color: #495057;
    margin: 0;
    white-space: nowrap;
}

.tab-select {
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    background: #ffffff;
    color: #495057;
    border: 1px solid #ced4da;
    border-radius: 6px;
    cursor: pointer;
    min-width: 160px;
    transition: all 0.2s ease;
    margin-left: 8px;
}

.tab-select:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.tab-select:hover {
    border-color: #adb5bd;
}

/* 엑셀 섹션 */
.excel-section {
    display: flex;
    gap: 8px;
}

.excel-btn {
    padding: 8px 16px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    border: none;
    border-radius: 6px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #ffffff;
    min-width: 100px;
}

.excel-btn:hover {
    transform: translateY(-1px);
}

.excel-btn.upload {
    background: #28a745;
}

.excel-btn.upload:hover {
    background: #218838;
}

.excel-btn.download {
    background: #007bff;
}

.excel-btn.download:hover {
    background: #0056b3;
}

.sub-tab-content-minimal {
    background: transparent;
    min-height: 500px;
    border: none;
    border-radius: 0;
}

.facility-history-content,
.facility-management-content {
    padding: 0;
}

/* NetworkOverview.vue 의 프로그래스바 스타일 참조 */
.progress-bar-container {
    display: flex;
    flex-direction: column;
    position: relative;
    flex: 1;
}

.custom-progress {
    border-radius: 20px;
    background-color: #e9ecef;
    overflow: hidden;
    position: relative;
    box-shadow:
        rgba(14, 63, 126, 0.04) 0px 0px 0px 1px,
        rgba(42, 51, 69, 0.04) 0px 1px 1px -0.5px,
        rgba(42, 51, 70, 0.04) 0px 3px 3px -1.5px,
        rgba(42, 51, 70, 0.04) 0px 6px 6px -3px,
        rgba(14, 63, 126, 0.04) 0px 12px 12px -6px,
        rgba(14, 63, 126, 0.04) 0px 24px 24px -12px;
}

.custom-progress .progress-bar {
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    transition: width 0.3s ease;
    background-color: var(--progress-color, #6c757d) !important;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding-left: 8px;
    color: white;
    height: 100%;
    box-shadow:
        rgba(0, 0, 0, 0.17) 0px -23px 25px 0px inset,
        rgba(0, 0, 0, 0.15) 0px -36px 30px 0px inset,
        rgba(0, 0, 0, 0.1) 0px -79px 40px 0px inset,
        rgba(0, 0, 0, 0.06) 0px 2px 1px,
        rgba(0, 0, 0, 0.09) 0px 4px 2px,
        rgba(0, 0, 0, 0.09) 0px 8px 4px,
        rgba(0, 0, 0, 0.09) 0px 16px 8px,
        rgba(0, 0, 0, 0.09) 0px 32px 16px;
}

.custom-progress:hover .progress-bar {
    opacity: 0.9;
}
</style>
